import { BranchQuote } from '../../api/dealer-api-interface-franchisee';
import { CurrencyCode, InputCreateQuote, QuoteType } from '../../api/dealer-api-interface-quote';
import { currentQuoteSupplierProvider } from '../../quotes/data/quoteSupplierProvider';
import { emptyGuid, newGuid } from '../../../webmodule-common/other/api/guid';
import { FranchiseeQuoteContainer } from './data/franchisee-quote-container';
import { getApiFactory } from '../../api/api-injector';
import { getQuoteSuppliers } from '../../quotes/quote-service';
import { isEmptyOrSpace } from '../../../webmodule-common/other/ui/string-helper-functions';
import { LangQuoteCreator } from '../../../localization/scripts/dealer-franchisee/quotes/quote-creator-lang';
import { localDateToServer } from '../../../webmodule-common/other/datetime-converter';
import { QuoteContainerManager } from '../../quotes/data/quote-container';
import { showError } from '../../../webmodule-common/other/ui/show-error';
import { toJsonStr } from '../../../webmodule-common/other/blob-converters';
import { userDataStore } from '../common/current-user-data-store';
import { v6ConfigSupplierOptions } from '../../../webmodule-common/interop/interfaces/v6-config';

export interface createFranchiseeQuoteOptions {
  supplierId: string;
  owner: string;
}

/**
 * Converts a currency string code (e.g., 'USD', 'EUR') to CurrencyCode enum value.
 * Returns USD as default if the code is not found.
 */
function getCurrencyCodeFromString(currencyString: string): CurrencyCode {
  const upperCode = currencyString?.toUpperCase();
  // Find the enum key that matches the currency string
  const enumKey = Object.keys(CurrencyCode).find(key => key === upperCode);
  if (enumKey) {
    return CurrencyCode[enumKey as keyof typeof CurrencyCode];
  }
  // Default to USD if not found
  return CurrencyCode.USD;
}

export async function createNewQuote(options: createFranchiseeQuoteOptions): Promise<FranchiseeQuoteContainer | null> {
  if (!isEmptyOrSpace(options.owner)) {
    try {
      //Shane: For initial release no modal, just create a quote
      //Kyley - Defaults here dont matter. defaults will be updated
      //via ui changing client settings etc

      const serviceProviderData = await QuoteContainerManager.createQuoteServiceProviderData(
        currentQuoteSupplierProvider,
        options.supplierId,
        options.owner
      );
      const api = getApiFactory().quote();
      const supplieroptions = v6ConfigSupplierOptions(options.supplierId);

      // Extract currency from supplier options
      const supplierCurrencyCode = supplieroptions.customOptions?.supplier?.globalization?.currency?.['code'];
      const quoteCurrency = supplierCurrencyCode ? getCurrencyCodeFromString(supplierCurrencyCode) : CurrencyCode.USD;

      const inputCreateQuote: InputCreateQuote = {
        id: newGuid(), //change this to use server side guid fetch
        quoteType: QuoteType.Quote,
        title: LangQuoteCreator.newQuote(),
        quoteOwnerId: options.owner,
        validUntilDate: null,
        termsAndConditions: '',
        excludeShippingLineItem: !supplieroptions.includeShipping,
        shippingTitle: LangQuoteCreator.shipping(),
        shippingDescription: LangQuoteCreator.totalShippingCost(),
        supplierId: options.supplierId,
        serviceProvider: currentQuoteSupplierProvider,
        assets: {
          data: {
            assetPath: api.createQuoteAssetPath('data', '.json'),
            data: toJsonStr(serviceProviderData) ?? null,
            delete: false,
            dataBase64: null,
            hasContent: false
          }
        },
        serviceProviderData: null,
        taxPercentage: userDataStore.defaultTaxRate,
        allowNonTaxableItems: userDataStore.config.allowNonTaxableItems,
        quoteCurrency: quoteCurrency
      };

      if (!userDataStore.defaultBranch) await userDataStore.loadCoreDetails();
      if (!userDataStore.defaultBranch) {
        await showError(LangQuoteCreator.cannotCreateQuoteNotAssignedToBranch());
        return null;
      }

      const quoteResult = await api.createQuote(inputCreateQuote);

      //TODO - we are only going to allow one kind of provider per quote
      // and only allow one kind of supplier per quote
      // so at some point, we need to add to the Quote/or Branch Quote
      // some limitation fields.. is ProviderType "v6"/"v7" and "supplierref"

      if (!quoteResult) return null;
      const branchQuote: BranchQuote = {
        branchId: userDataStore.defaultBranch.id,
        globalSupplierSupplierId: quoteResult.quote.supplierId,
        quoteSetId: quoteResult.quote.quoteSetId,
        //Use the exact same id for the branch quote. makes it easy to extend
        id: quoteResult.quote.id,
        clientId: emptyGuid,
        clientName: '',
        clientTypeId: emptyGuid,
        contactId: emptyGuid,
        clientTypeName: '',
        contactName: '',
        dateCreated: localDateToServer(new Date()),
        recordVersion: ''
      };

      //if there is any reason we can't create this, we wont worry about it now, if we already have the quote
      //created. we will just attempt to create it later. if it cant be done later, it is probably
      //a system failure.
      const branchQuoteResult = await getApiFactory().franchisee().createBranchQuote({ branchQuote: branchQuote });

      //construct new quote container for an empty new quote
      return new FranchiseeQuoteContainer(
        quoteResult.quote.id,
        quoteResult.quote,
        quoteResult.quotePrice,
        quoteResult.quotePresentation,
        null,
        null, //we want to force load any potential server generated items
        null,
        null,
        null,
        branchQuoteResult?.branchQuote ?? branchQuote,
        serviceProviderData,
        true
      );
    } catch {
      return null;
    }
  } else {
    //owner is defined as the Branch of the user.
    await showError(LangQuoteCreator.cannotCreateQuoteNotAssignedToBranch());
    return null;
  }
}

export async function checkSuppliersIsOnline(supplierId: string): Promise<boolean> {
  //force a reload of the supplier list when we want to create quotes, to check the online state of things.
  const suppliers = await getQuoteSuppliers();
  const supplier = suppliers.find(x => x.supplierId === supplierId);
  return supplier?.online ?? false;
}
